import React, { useState, useEffect, useRef } from 'react';
import {
  Text,
  Input,
  Field,
  Button,
} from '@fluentui/react-components';
import { useStyles } from './ConnectDataProvider.styles';
import { OnboardingStepWrapper } from './OnboardingStepWrapper';
import { OnboardingData } from '../../pages/OnboardingFlow/OnboardingFlow';
import { Key24Regular } from '@fluentui/react-icons';
import useEventTracker from '../../hooks/useEventTracker';
import { ElementButton, PageName } from '../../tracking/tracking.types';

type ConnectDataProviderProps = {
  onNext: (data: Pick<OnboardingData, 'connectDataProvider'>) => void;
  onBack: () => void;
  currentStep: number;
  totalSteps: number;
}

export const ConnectDataProvider: React.FC<ConnectDataProviderProps> = ({ onNext, onBack, currentStep, totalSteps }) => {
  const styles = useStyles();
  const [providerKey, setProviderKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  const eventTracker = useEventTracker();
  const pageLoadStartTime = useRef<number>(performance.now());
  const hasTrackedPageLoad = useRef<boolean>(false);

  useEffect(() => {
    if (!hasTrackedPageLoad.current && eventTracker.isUsageTrackingInitialized) {
      const loadTimeMs = Math.round(performance.now() - pageLoadStartTime.current);
      eventTracker.eventTrackOnPageLoaded({
        pageName: PageName.ONBOARDING_API_KEY,
        loadTimeMs,
      });
      hasTrackedPageLoad.current = true;
    }
  }, [eventTracker.isUsageTrackingInitialized]);

  const handleOnContinue = () => {
    if (providerKey) {
      eventTracker.eventTrackOnElementClicked({
        pageName: PageName.ONBOARDING_API_KEY,
        elementName: ElementButton.SUBMIT_API_KEY,
        elementType: 'button',
      });
      onNext({ connectDataProvider: { providerKey } });
    } else {
      setError('API key is required');
    }
  }

  const handleOnBack = () => {
    eventTracker.eventTrackOnElementClicked({
      pageName: PageName.ONBOARDING_API_KEY,
      elementName: ElementButton.BACK,
      elementType: 'button',
    });
    onBack();
  }

  return (
    <OnboardingStepWrapper 
      title="Connect to a data provider"
      currentStep={currentStep}
      totalSteps={totalSteps}
    >
      <Text data-testid="onboarding-connect-description">Connect ADM to a third party data provider to receive policy updates for your Teams External Collaboration policies.</Text>
      <div className={styles.providerKeySection} data-testid="onboarding-connect-provider-key-section">
        <Field 
          label="API key" 
          required
          hint="Authentication key provided by the domain feed provider"
          validationState={error ? "error" : "none"}
          validationMessage={error || undefined}
          data-testid="onboarding-connect-api-key-field"
        >
          <Input
            type={showApiKey ? 'text' : 'password'}
            placeholder="Enter your Domain Feed API key"
            value={providerKey} 
            onChange={(e) => {
              setError(null);
              setProviderKey(e.target.value);
            }}
            contentBefore={<Key24Regular />}
            data-testid="onboarding-api-key-input"
            contentAfter={
              <div style={{ display: 'flex', gap: '4px' }} data-testid="onboarding-connect-api-key-actions">
                <Button
                  appearance="transparent"
                  size="small"
                  onClick={() => setShowApiKey(!showApiKey)}
                  data-testid="onboarding-connect-toggle-api-key-visibility"
                >
                  {showApiKey ? 'Hide' : 'Show'}
                </Button>
              </div>
            }
          />
        </Field>
      </div>
      <div className={styles.buttonContainer} data-testid="onboarding-connect-button-container">
        <Button appearance="secondary" disabled={currentStep === 0} onClick={handleOnBack} data-testid="onboarding-back-button">Back</Button> {/* Back button will go to terms and conditions*/}
        <Button appearance="primary" disabled={Boolean(error)} onClick={handleOnContinue} data-testid="onboarding-continue-button">Continue</Button>
      </div>
    </OnboardingStepWrapper>
  );
}